// @ts-strict-ignore
import React, { ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RecaptchaScoreError } from 'recaptcha/RecaptchaScoreError'
import { RecaptchaTokenError } from 'recaptcha/RecaptchaTokenError'
import { isRecapthcaLowScoreError } from 'recaptcha/utils'
import { ActionList, Avatar, Button, Icon } from '@node-space/ui-components'
import { logError } from '@node-space/utils'
import { SubmitError } from 'components/Form/SubmitError'
import LoginPageLayout, { LoginPageLayoutVariant } from 'components/layout/LoginPageLayout'
import { useLoginMutation } from 'hooks/mutations'
import { PostWorkplaceAuthReq } from 'types/authentication/PostLoginReq'
import { BaseErrorResponse } from 'types/beneficiaries'
import { WorkspaceListItem } from 'types/types'
import { LoginSharedProps } from './types'

interface WorkplacesAuthProps extends LoginSharedProps {
  workspaceList: WorkspaceListItem[]
  setRequiresUserSelection: (value: boolean) => void
  setShowTwoFactor: (value: boolean) => void
}

const WorkplacesAuth = ({
  workspaceList,
  loginData,
  recaptcha,
  setShowTwoFactor,
  setRequiresUserSelection,
  setSuccessfulLogin,
}: WorkplacesAuthProps) => {
  const { t } = useTranslation()

  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false)
  const [selectedWorkspace, setSelectedWorkspace] = useState<string | null>(null)

  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<ReactNode>('')

  const { mutate: postLogin } = useLoginMutation()

  const loginWithWorkspace = async (value: string) => {
    setSelectedWorkspace(value)
    setIsLoadingSubmit(true)
    setShowError(false)
    setErrorMessage('')

    const recaptchaToken = await recaptcha?.getToken()

    if (!recaptchaToken) {
      setIsLoadingSubmit(false)
      setErrorMessage(<RecaptchaTokenError />)
      setShowError(true)
      return
    }

    const postData: PostWorkplaceAuthReq = {
      ...loginData,
      recaptcha: recaptchaToken,
      accountId: parseInt(value),
    }

    postLogin(postData, {
      onSuccess: async response => {
        await setSuccessfulLogin(response)
      },
      onError: async (error: BaseErrorResponse) => {
        const errorMessage = error?.data?.message
        if (error.status === 412) {
          setShowTwoFactor(true)
          setRequiresUserSelection(false)
        } else {
          if (isRecapthcaLowScoreError(errorMessage)) {
            setErrorMessage(<RecaptchaScoreError />)
          } else {
            setErrorMessage(t('errorMessages.login.default'))
            if (error.status !== 401) {
              logError(
                `Error from WorkplacesAuth - postLogin${errorMessage ? ` - ${errorMessage}` : ''}`,
                error,
                JSON.stringify(postData, null, 3),
                'fatal'
              )
            }
          }
          setShowError(true)
        }
      },
      onSettled: async () => {
        setIsLoadingSubmit(false)
      },
    })
  }

  const isLoading = useMemo(() => {
    return recaptcha?.isLoading || isLoadingSubmit
  }, [recaptcha?.isLoading, isLoadingSubmit])

  const workspaceListItems = useMemo(() => {
    return workspaceList?.map(workspace => {
      return {
        key: workspace?.value,
        id: workspace?.value,
        title: workspace?.label,
        Avatar: <Avatar type="entity" name={workspace?.label} size="small" />,
        CustomAction: (
          <Button
            variant="ghost"
            onClick={() => !isLoading && loginWithWorkspace(workspace?.value)}
            iconElement={<Icon name="CaretRight" size="medium" />}
            loading={isLoading && selectedWorkspace === workspace?.value}
          />
        ),
        onItemClick: () => !isLoading && loginWithWorkspace(workspace?.value),
      }
    })
  }, [workspaceList, isLoading])

  return (
    <LoginPageLayout
      title={t('welcomeBack')}
      description={t('workspaceSelection')}
      variant={LoginPageLayoutVariant.WIDE}
    >
      {showError && <SubmitError>{errorMessage}</SubmitError>}
      <ActionList items={workspaceListItems} id="workspace-list" />
    </LoginPageLayout>
  )
}

export default WorkplacesAuth
